"use client";

import {
  ArrowRightIcon,
  MapPinIcon,
  SparklesIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { Button } from "@heroui/button";
import {
  Chip,
  cn,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/react";
import { withTranslationsClient } from "@khol/components/hoc";
import { type CoreComponentProps } from "@khol/utils/pages/core-component";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useMemo, useState } from "react";

import { useUser } from "@/hooks/useUser";
import {
  DEFAULT_MAX_AGE_DAYS,
  resolveAudience,
  selectAnnouncements,
  type FeatureAnnouncement,
} from "./announcements";
import translations from "./index.translations";
import { useSeenAnnouncements } from "./useSeenAnnouncements";

/**
 * How long one step must be on screen before that announcement counts as read.
 *
 * Deliberately does not close the modal or advance the stepper — the reader decides when they are
 * done. It only stops that announcement being offered again, so someone who reads it and then
 * navigates away, or closes the tab, is not shown it a second time.
 */
export const SEEN_AFTER_MS = 60_000;

/**
 * Routes where a "what's new" modal would land on top of something more urgent. Signing in is the
 * one thing a visitor is mid-way through that must not be interrupted.
 */
const SUPPRESSED_ROUTES = [/\/sign-in(\/|$)/, /\/sign-up(\/|$)/];

/** Flattened translations: plain strings, plus `stepOf`, which takes the step numbers. */
type Texts = Record<string, string> & {
  stepOf: (current: number, total: number) => string;
};

/** Reads one announcement's copy out of the flattened texts by the `<id><Field>` convention. */
function announcementTexts(texts: Texts, { id }: FeatureAnnouncement) {
  return {
    title: texts[`${id}Title`],
    body: texts[`${id}Body`],
    where: texts[`${id}Where`],
    cta: texts[`${id}Cta`],
  };
}

interface AnnouncementModalProps {
  locale: string;
  texts: Texts;
  /** Frozen by the caller: this list does not shrink while the reader is in the modal. */
  announcements: readonly FeatureAnnouncement[];
  markSeen: (announcementIds: readonly string[]) => void;
}

/**
 * One announcement per step, so each gets the whole modal rather than a share of a list.
 *
 * Only the steps the reader has actually reached are recorded as seen. Closing at step 1 of 3
 * therefore leaves steps 2 and 3 to be offered on the next page, rather than retiring news nobody
 * has laid eyes on — which is the point of stepping through them one at a time.
 */
function AnnouncementModal({
  locale,
  texts,
  announcements,
  markSeen,
}: AnnouncementModalProps) {
  const [isOpen, setIsOpen] = useState(true);
  const [step, setStep] = useState(0);
  /** Furthest step reached. Going Back does not un-see an announcement, so this only grows. */
  const [furthest, setFurthest] = useState(0);

  const ids = useMemo(() => announcements.map(({ id }) => id), [announcements]);
  const total = announcements.length;
  const isLast = step === total - 1;
  const current = announcements[step];
  const reached = useMemo(() => ids.slice(0, furthest + 1), [ids, furthest]);

  const currentId = current?.id;
  useEffect(() => {
    if (!currentId) return;
    const timer = setTimeout(() => markSeen([currentId]), SEEN_AFTER_MS);
    return () => clearTimeout(timer);
  }, [currentId, markSeen]);

  function goTo(next: number) {
    setStep(next);
    setFurthest((current) => Math.max(current, next));
  }

  function close() {
    setIsOpen(false);
    markSeen(reached);
  }

  if (!current) return null;

  const copy = announcementTexts(texts, current);

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      scrollBehavior="inside"
      size="lg"
      placement="center"
      // The default close button carries an English aria-label; this one is translated. HeroUI
      // clones it and attaches the close handler.
      closeButton={
        <button type="button" aria-label={texts.dismiss}>
          <XMarkIcon className="h-4 w-4" />
        </button>
      }
    >
      <ModalContent>
        <ModalHeader className="flex items-center gap-2">
          <SparklesIcon className="h-5 w-5 text-primary-600" />
          {texts.modalTitle}
        </ModalHeader>

        <ModalBody className="gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <Chip size="sm" color="primary" variant="flat">
              {texts.heading}
            </Chip>
            <h3 className="font-semibold text-foreground">{copy.title}</h3>
          </div>

          <p className="text-sm text-default-600">{copy.body}</p>

          <p className="flex items-start gap-1.5 text-xs text-default-500">
            <MapPinIcon className="mt-px h-4 w-4 shrink-0" />
            <span>{copy.where}</span>
          </p>

          {current.path && (
            <Link
              href={`/${locale}${current.path}`}
              onClick={close}
              className="mt-1 inline-flex w-fit items-center gap-1.5 rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              {copy.cta}
              {/* Hebrew renders the page RTL, where a "forward" arrow points the other way. */}
              <ArrowRightIcon className="h-4 w-4 rtl:rotate-180" />
            </Link>
          )}
        </ModalBody>

        <ModalFooter className="items-center justify-between">
          {/* The dots carry the progress visually; the label carries it for a screen reader. */}
          {total > 1 ? (
            <div
              className="flex items-center gap-1.5"
              role="group"
              aria-label={texts.stepOf(step + 1, total)}
            >
              {announcements.map((announcement, index) => (
                <span
                  key={announcement.id}
                  className={cn(
                    "h-1.5 rounded-full transition-all",
                    index === step ? "w-4 bg-primary-600" : "w-1.5 bg-default-300",
                  )}
                />
              ))}
            </div>
          ) : (
            <span />
          )}

          <div className="flex gap-2">
            {step > 0 && (
              <Button variant="flat" onPress={() => goTo(step - 1)}>
                {texts.back}
              </Button>
            )}
            {isLast ? (
              <Button color="primary" onPress={close}>
                {texts.acknowledge}
              </Button>
            ) : (
              <Button color="primary" onPress={() => goTo(step + 1)}>
                {texts.next}
              </Button>
            )}
          </div>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export interface NewFeatureAnnouncementProps extends CoreComponentProps {
  /** How many announcements one modal may carry. */
  limit?: number;
  /** Overrides how long an announcement counts as new. Mainly a seam for tests and demos. */
  maxAgeDays?: number;
}

/**
 * Offers a visitor the features that are new *to them*, in a modal, and tells them where to find
 * each one.
 *
 * Mounted once in the root layout, which survives client-side navigation — so the "already offered
 * here" state is keyed to the pathname, and an announcement nobody has acknowledged is offered
 * again on the next page. What each role is shown comes from `announcements.ts`; what has already
 * been seen comes from `useSeenAnnouncements`.
 */
function NewFeatureAnnouncementComponent({
  locale,
  texts,
  limit = 3,
  maxAgeDays = DEFAULT_MAX_AGE_DAYS,
}: NewFeatureAnnouncementProps & { texts: Texts }) {
  const pathname = usePathname();
  const { user, isSignedIn, isLoading } = useUser();
  const { seen, markSeen } = useSeenAnnouncements(!!isSignedIn);

  const suppressed = SUPPRESSED_ROUTES.some((route) => route.test(pathname));

  /**
   * What this page would offer. `null` while the role or the seen list is still unknown, so nothing
   * is offered on a guess.
   */
  const pending = useMemo(() => {
    if (suppressed || isLoading || seen === null) return null;

    return selectAnnouncements({
      audience: resolveAudience({ isSignedIn: !!isSignedIn, role: user?.role }),
      seenIds: seen,
      maxAgeDays,
      limit,
    });
  }, [suppressed, isLoading, seen, isSignedIn, user?.role, maxAgeDays, limit]);

  /**
   * The set the modal opened with, pinned to the page it opened on.
   *
   * Pinning matters twice over. Freezing the list keeps marking it seen — by the timer or by
   * closing — from emptying the modal while the reader is still in it, which would amount to
   * closing it for them. Keying it to the path is what re-offers an unacknowledged announcement on
   * the next page, since this component itself never unmounts.
   */
  const [session, setSession] = useState<{
    path: string;
    announcements: readonly FeatureAnnouncement[];
  } | null>(null);

  useEffect(() => {
    if (pending === null) return;

    setSession((current) => {
      if (current?.path === pathname) return current;
      return pending.length > 0 ? { path: pathname, announcements: pending } : null;
    });
  }, [pathname, pending]);

  if (!session) return null;

  return (
    <AnnouncementModal
      key={session.path}
      locale={locale ?? "en"}
      texts={texts}
      announcements={session.announcements}
      markSeen={markSeen}
    />
  );
}

export const NewFeatureAnnouncement = withTranslationsClient<
  NewFeatureAnnouncementProps,
  typeof translations
>(NewFeatureAnnouncementComponent, translations);

export default NewFeatureAnnouncement;
